import { useEffect, useState } from "react";
import { X, Download } from "lucide-react";

export default function PWAInstallPrompt() {
    const [prompt, setPrompt] = useState(null);
    const [show, setShow] = useState(false);
    const [installed, setInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia("(display-mode: standalone)").matches) {
            setInstalled(true);
            return;
        }

        // Check if already dismissed
        if (sessionStorage.getItem("pwa-dismissed")) return;

        // Check iOS
        const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(ios);

        if (ios) {
            // Show iOS guide after 30 seconds
            setTimeout(() => setShow(true), 30000);
        }

        // Android install prompt
        window.addEventListener("beforeinstallprompt", (e) => {
            e.preventDefault();
            setPrompt(e);
            setTimeout(() => setShow(true), 30000);
        });

        window.addEventListener("appinstalled", () => {
            setInstalled(true);
            setShow(false);
        });
    }, []);

    const install = async () => {
        if (!prompt) return;
        prompt.prompt();
        const result = await prompt.userChoice;
        if (result.outcome === "accepted") setInstalled(true);
        setShow(false);
    };

    const dismiss = () => {
        setShow(false);
        sessionStorage.setItem("pwa-dismissed", "1");
    };

    if (installed || !show) return null;

    // iOS guide
    if (isIOS) return (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-white rounded-2xl shadow-floating border border-slate-100 p-4">
            <button onClick={dismiss} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"><X size={16} /></button>
            <div className="flex items-center gap-3 mb-3">
                <img src="/favicon-192.png" alt="GiftToCash" className="w-12 h-12 rounded-xl" />
                <div>
                    <div className="font-bold text-slate-900 text-sm">Install GiftToCash</div>
                    <div className="text-xs text-slate-500">Add to your home screen</div>
                </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600 space-y-2">
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-blue-500 text-white rounded flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    Tap the <strong>Share</strong> button at the bottom of Safari
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-blue-500 text-white rounded flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    Scroll down and tap <strong>"Add to Home Screen"</strong>
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-blue-500 text-white rounded flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    Tap <strong>"Add"</strong> to confirm
                </div>
            </div>
        </div>
    );

    // Android prompt
    return (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-white rounded-2xl shadow-floating border border-slate-100 p-4">
            <button onClick={dismiss} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"><X size={16} /></button>
            <div className="flex items-center gap-3 mb-4">
                <img src="/favicon-192.png" alt="GiftToCash" className="w-12 h-12 rounded-xl" />
                <div>
                    <div className="font-bold text-slate-900 text-sm">Install GiftToCash</div>
                    <div className="text-xs text-slate-500">Fast access from your home screen</div>
                </div>
            </div>
            <div className="flex gap-2">
                <button onClick={dismiss}
                    className="flex-1 py-2 rounded-full border border-slate-200 text-slate-600 text-sm font-medium">
                    Not now
                </button>
                <button onClick={install}
                    className="flex-1 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
                    <Download size={14} /> Install
                </button>
            </div>
        </div>
    );
}